import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { useAuthStore } from '@/core/stores/auth.store';
import { cn } from '@/core/lib/utils';
import { formatTimeAgo } from '@/shared/utils/formatTimeAgo';
import i18n from '@/core/i18n/config';
import { useNoteHistory } from '../hooks/useNoteHistory';
import { useRestoreNoteVersion } from '../hooks/useNoteVersionMutations';

interface NoteHistoryDialogProps {
  noteId: string;
  noteTitle: string | null;
  /** The note's own `updatedAt` — renders the synthetic "Actual" row, since
   *  `GET /notes/{id}/history` never includes the live current state, only past snapshots. */
  noteUpdatedAt: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRestored: () => void;
  /** Opens a read-only preview of that version in the main content area — never mutates the note. */
  onViewVersion: (versionId: string) => void;
}

export function NoteHistoryDialog({
  noteId,
  noteTitle,
  noteUpdatedAt,
  open,
  onOpenChange,
  onRestored,
  onViewVersion,
}: NoteHistoryDialogProps) {
  const { t } = useTranslation('notes');
  const user = useAuthStore((s) => s.user);
  const { data, isLoading, isError } = useNoteHistory(open ? noteId : undefined);
  const restoreVersion = useRestoreNoteVersion(noteId);

  const versions = data?.items ?? [];

  const handleRestore = async (versionId: string) => {
    await restoreVersion.mutateAsync(versionId);
    onOpenChange(false);
    onRestored();
  };

  const handleView = (versionId: string) => {
    onOpenChange(false);
    onViewVersion(versionId);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid="notes-history-dialog">
        <DialogHeader>
          <DialogTitle>{t('history.title')}</DialogTitle>
          <DialogDescription>
            {t('history.subtitle', { title: noteTitle || t('editor.titlePlaceholder') })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex max-h-96 flex-col gap-1 overflow-y-auto">
          {/* Synthetic "current" row — read from the note itself, never from history (see prop doc). Display-only, not clickable. */}
          <div
            className="flex items-center gap-3 rounded-md bg-primary/5 px-2 py-3"
            data-testid="notes-history-current-row"
          >
            <UserAvatar
              name={user?.name ?? ''}
              color={user?.color || 'indigo-deep'}
              src={user?.avatarUrl}
              size="sm"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium text-foreground">{user?.name}</span>
                <Badge
                  variant="outline"
                  className="border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950 dark:text-green-400"
                >
                  {t('history.current')}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  · {formatTimeAgo(noteUpdatedAt, i18n.language)}
                </span>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div aria-hidden="true" data-testid="notes-history-loading-skeleton">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-2 py-3">
                  <Skeleton className="h-7 w-7 rounded-full" />
                  <Skeleton className="h-4 flex-1" />
                </div>
              ))}
            </div>
          ) : isError ? (
            <p
              className="px-2 py-4 text-sm text-destructive"
              data-testid="notes-history-error-message"
            >
              {t('history.error')}
            </p>
          ) : versions.length === 0 ? (
            <p
              className="px-2 py-4 text-sm text-muted-foreground"
              data-testid="notes-history-empty-state"
            >
              {t('history.empty')}
            </p>
          ) : (
            versions.map((version) => (
              <button
                key={version.id}
                type="button"
                onClick={() => version.id && handleView(version.id)}
                className={cn(
                  'flex items-center gap-3 rounded-md px-2 py-3 text-left transition-colors',
                  'hover:bg-muted/60',
                )}
                data-testid={`notes-history-version-row-${version.id}`}
              >
                <UserAvatar
                  name={user?.name ?? ''}
                  color={user?.color || 'indigo-deep'}
                  src={user?.avatarUrl}
                  size="sm"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-foreground">
                      {user?.name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      · {formatTimeAgo(version.createdAt, i18n.language)}
                    </span>
                  </div>
                </div>
                {version.id && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleRestore(version.id as string);
                    }}
                    disabled={restoreVersion.isPending}
                    data-testid={`notes-history-restore-button-${version.id}`}
                  >
                    {t('history.restore')}
                  </Button>
                )}
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
